import { message as antMessage } from 'antd';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { deleteChat as deleteChatApi, getBulkChatStatus, getChatMetadata, getChats } from '../services/chatApi';
import { purgeChatDraft } from '../utils/editorStateStorage';
import eventBus from '../utils/eventBus';
import NotificationSoundManager from '../utils/NotificationSoundManager';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';

const ChatListContext = createContext();

export const ChatListProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    hasMore: false
  });
  const { currentUser, userProfile } = useAuth();
  // Notifications v1: the viewer's CellCog user id, readable from the long-lived WS handler below.
  const profileIdRef = useRef(null);
  useEffect(() => { profileIdRef.current = userProfile?.id || null; }, [userProfile?.id]);

  // Project filter state
  const [projectFilter, setProjectFilter] = useState('all');
  const [availableProjects, setAvailableProjects] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Track selected chat for initial load
  const selectedChatIdRef = useRef(null);
  const reconciliationTimeoutRef = useRef(null);
  const operatingChatsPollingRef = useRef(null);
  const chatsRef = useRef(chats);

  // Persist sidebar scroll position across route-driven unmount/remount cycles.
  // This ref lives in ChatListContext (which never unmounts) so the value survives
  // even when GlobalSidebar is destroyed and recreated during route transitions.
  const sidebarScrollPositionRef = useRef(0);

  // Keep chatsRef in sync with latest chats state
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Extract unique projects from loaded chats
  useEffect(() => {
    const projectsMap = new Map();
    chats.forEach(chat => {
      if (chat.project_id && chat.project_name) {
        projectsMap.set(chat.project_id, {
          id: chat.project_id,
          name: chat.project_name
        });
      }
    });
    setAvailableProjects(Array.from(projectsMap.values()));
  }, [chats]);

  const fetchChats = useCallback(async (page = 1, isFilterChange = false) => {
    if (!currentUser || userProfile?.onboarding_status !== 'JOINED') {
      setChats([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      // Show appropriate loading state
      if (isFilterChange) {
        setFilterLoading(true);
        setChats([]); // Clear existing chats on filter change
      } else {
        setLoading(true);
      }

      // Fix the project parameter logic
      let projectParam = null;
      if (projectFilter === 'personal') {
        projectParam = ''; // Empty string for personal chats
      } else if (projectFilter !== 'all') {
        projectParam = projectFilter; // Actual project ID
      }
      // For 'all', projectParam remains null (no filter)

      const response = await getChats(page, pagination.pageSize, projectParam);

      const { chats: fetchedChats, total, has_more, page_size } = response.data;

      // NEW: If this is first page and we have a selected chat, check if we need to add it
      let finalChats = fetchedChats;

      if (page === 1 && selectedChatIdRef.current) {
        const selectedId = selectedChatIdRef.current;
        const chatExists = fetchedChats.some(c => c.id === selectedId);

        if (!chatExists) {
          try {
            const response = await getChatMetadata(selectedId);
            const selectedChat = response.data;
            if (selectedChat) {
              // Add selected chat at the beginning
              finalChats = [selectedChat, ...fetchedChats];
            }
          } catch (error) {
            console.error('[ChatList] Failed to fetch selected chat:', error);
          }
        }
      }

      // Update the chats state - single update with final data
      setChats(prevChats => {
        return (isFilterChange || page === 1) ? finalChats : [...prevChats, ...finalChats];
      });

      setPagination({
        page,
        pageSize: page_size,
        total,
        hasMore: has_more
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching chats:', err);

      // Special handling for access errors
      if (err.isCellCogAccessError) {
        setError('CellCog access not enabled for this user');
      } else {
        setError("Couldn't load your conversations right now.");
      }
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  }, [currentUser, userProfile?.onboarding_status, pagination.pageSize, projectFilter]);

  const loadMoreChats = useCallback(() => {
    if (pagination.hasMore && !loading && !filterLoading) {
      fetchChats(pagination.page + 1);
    }
  }, [pagination.hasMore, pagination.page, loading, filterLoading, fetchChats]);

  const refreshChats = useCallback(() => {
    fetchChats(1);
  }, [fetchChats]);

  // Handle project filter change
  const handleProjectFilterChange = useCallback((newFilter) => {
    setProjectFilter(newFilter);
  }, []);

  // Fetch chats when filter changes
  useEffect(() => {
    if (currentUser && userProfile?.onboarding_status === 'JOINED' && projectFilter !== undefined) {
      fetchChats(1, true); // isFilterChange = true
    }
  }, [projectFilter, currentUser, userProfile?.onboarding_status]); // Note: fetchChats is intentionally not included to avoid loops

  const updateChatInList = useCallback((updatedChat) => {
    setChats(prevChats => {
      // Merge updated fields into existing chat (preserves fields not in the update)
      return prevChats.map(chat =>
        chat.id === updatedChat.id ? { ...chat, ...updatedChat } : chat
      );
    });
  }, []);

  const addChatToList = useCallback((newChat) => {
    // Check if chat already exists in the list
    setChats(prevChats => {
      const chatExists = prevChats.some(chat => chat.id === newChat.id);

      if (chatExists) {
        return prevChats.map(chat =>
          chat.id === newChat.id ? newChat : chat
        );
      } else {
        return [newChat, ...prevChats];
      }
    });

    setPagination(prev => ({
      ...prev,
      total: prev.total + 1
    }));
  }, []);

  // NEW: Delete chat function
  const deleteChat = useCallback(async (chatId) => {
    try {
      const response = await deleteChatApi(chatId);
      // Don't remove from list yet - wait for WebSocket CHAT_DELETED event
      return response.data;
    } catch (error) {
      console.error('[ChatList] Delete chat error:', error);
      throw error;
    }
  }, []);

  // Get a chat by ID if needed
  const getChatById = useCallback(async (chatId) => {
    if (!chatId) return null;

    try {
      const response = await getChatMetadata(chatId);
      return response.data;
    } catch (err) {
      console.error('Error fetching chat by ID:', err);
      return null;
    }
  }, []);

  // Set selected chat ID for initial load
  const setSelectedChatId = useCallback((chatId) => {
    selectedChatIdRef.current = chatId;
  }, []);

  // Bulk reconcile chat statuses
  const reconcileChatListState = useCallback(async (reason) => {
    if (chats.length === 0) return;

    // Get up to 20 most recent chat IDs (bulk endpoint limit)
    const chatIdsToCheck = chats.slice(0, 20).map(chat => chat.id);

    try {
      const response = await getBulkChatStatus(chatIdsToCheck);
      const serverChats = response.data.chats;

      // Update chats that changed
      setChats(prevChats => {
        let hasChanges = false;

        const updatedChats = prevChats.map(chat => {
          const serverChat = serverChats[chat.id];

          // Skip if server doesn't have this chat
          if (!serverChat) return chat;

          // Check for changes in key fields
          const fieldsChanged =
            chat.operating !== serverChat.operating ||
            chat.name !== serverChat.name ||
            chat.lucide_icon !== serverChat.lucide_icon ||
            chat.thumbnail_url !== serverChat.thumbnail_url;

          if (fieldsChanged) {
            hasChanges = true;
            // Emit metadata update event for CurrentChatContext
            eventBus.emit('chat-metadata-updated', {
              chatId: chat.id,
              chat: serverChat
            });

            return serverChat;
          }

          return chat;
        });

        return hasChanges ? updatedChats : prevChats;
      });

    } catch (err) {
      console.error('[ChatList] Reconciliation error:', err);
      // Don't throw - reconciliation is best-effort
    }
  }, [chats]);

  // Setup reconciliation callback with UserStreamManager
  useEffect(() => {
    // Debounce wrapper
    const debouncedReconciliation = (reason) => {
      // Clear any pending reconciliation
      if (reconciliationTimeoutRef.current) {
        clearTimeout(reconciliationTimeoutRef.current);
      }

      // Debounce to avoid spam (500ms)
      reconciliationTimeoutRef.current = setTimeout(() => {
        reconcileChatListState(reason);
      }, 500);
    };

    // Only register if we have chats loaded
    if (chats.length > 0) {
      const unsubscribe = UserStreamManager.addReconciliationCallback(debouncedReconciliation);

      return () => {
        // Cleanup timeout
        if (reconciliationTimeoutRef.current) {
          clearTimeout(reconciliationTimeoutRef.current);
        }
        // Cleanup callback
        unsubscribe();
      };
    }
  }, [reconcileChatListState, chats.length]);

  // Poll all operating chats for completion detection (fallback)
  useEffect(() => {
    // Get operating chat IDs
    const operatingChatIds = chats
      .filter(chat => chat.operating)
      .slice(0, 20) // Bulk endpoint limit
      .map(chat => chat.id);

    // Clear existing polling
    if (operatingChatsPollingRef.current) {
      clearInterval(operatingChatsPollingRef.current);
      operatingChatsPollingRef.current = null;
    }

    // Only poll if we have operating chats
    if (operatingChatIds.length === 0) {
      return;
    }

    // Poll every 30s
    operatingChatsPollingRef.current = setInterval(async () => {
      // Re-calculate operating chats (may have changed)
      const currentOperating = chats
        .filter(chat => chat.operating)
        .slice(0, 20)
        .map(chat => chat.id);

      if (currentOperating.length === 0) {
        clearInterval(operatingChatsPollingRef.current);
        operatingChatsPollingRef.current = null;
        return;
      }

      try {
        const response = await getBulkChatStatus(currentOperating);
        const serverChats = response.data.chats;

        // Update chats that completed
        setChats(prevChats => {
          let hasChanges = false;

          const updated = prevChats.map(chat => {
            const serverChat = serverChats[chat.id];

            if (!serverChat) return chat;

            // Detect completion or HC turn
            if (chat.operating && !serverChat.operating) {
              hasChanges = true;

              // Notify on completion / HC turn — but skip if this stop is an HC
              // approval pause whose sound already played via
              // HUMAN_COMPUTER_COMMANDS_PENDING (mirrors the CHAT_STATUS_UPDATE
              // handler's dedupe below).
              const isHcPause = serverChat.human_computer_turn === true;
              if (!(isHcPause && NotificationSoundManager.hasPlayedRecently(chat.id))) {
                NotificationSoundManager.play(chat.id);
              }

              // Emit metadata update (triggers CurrentChatContext completion)
              eventBus.emit('chat-metadata-updated', {
                chatId: chat.id,
                chat: serverChat
              });

              return serverChat;
            }

            // Update other metadata changes too
            if (chat.name !== serverChat.name ||
              chat.lucide_icon !== serverChat.lucide_icon ||
              chat.thumbnail_url !== serverChat.thumbnail_url) {
              hasChanges = true;

              eventBus.emit('chat-metadata-updated', {
                chatId: chat.id,
                chat: serverChat
              });

              return serverChat;
            }

            return chat;
          });

          return hasChanges ? updated : prevChats;
        });

      } catch (err) {
        console.error('[ChatList] Polling error:', err);
        // Don't stop polling on transient errors
      }
    }, 30000);

    return () => {
      if (operatingChatsPollingRef.current) {
        clearInterval(operatingChatsPollingRef.current);
        operatingChatsPollingRef.current = null;
      }
    };
  }, [chats]); // Re-run when chats change

  // Subscribe to user stream updates
  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to chat status updates
    const unsubscribeStatus = UserStreamManager.subscribe('CHAT_STATUS_UPDATE', (data) => {
      const { chat_id, ...chatMetadata } = data;

      // Detect operating → not-operating transition for notification sound
      // Use chatsRef (not chats) to avoid stale closure — this effect doesn't re-subscribe on chats change
      const prevChat = chatsRef.current.find(c => c.id === chat_id);
      // Notify on operating → not-operating transition (both completion AND HC turn).
      // EXCEPTION: when the stop is an HC approval pause (human_computer_turn=true),
      // HumanComputerContext already played the sound on HUMAN_COMPUTER_COMMANDS_PENDING
      // if the user was viewing the chat — the two events are the same logical
      // "needs attention" moment, seconds apart (beyond the manager's 500ms debounce),
      // which caused a double ring. If no sound played yet (user was viewing a
      // different chat, so HumanComputerContext filtered the event), this remains
      // the one and only notification.
      if (prevChat?.operating && chatMetadata.operating === false) {
        const isHcPause = chatMetadata.human_computer_turn === true;
        // Notifications v1 (Sep 9 2026): the server names the ONE person this stop rings for (the human who messaged
        // in the run; null = nobody). An absent key = an older backend, keep ringing as before.
        const forMe = chatMetadata.attention_actor_id === undefined
          || (!!chatMetadata.attention_actor_id && chatMetadata.attention_actor_id === profileIdRef.current);
        if (forMe && !(isHcPause && NotificationSoundManager.hasPlayedRecently(chat_id))) {
          NotificationSoundManager.play(chat_id);
        }
      }

      // Update chat in list
      updateChatInList({
        id: chat_id,
        ...chatMetadata
      });

      // Emit to eventBus for CurrentChatContext
      eventBus.emit('chat-metadata-updated', {
        chatId: chat_id,
        chat: { id: chat_id, ...chatMetadata }
      });
    });

    // NEW: Subscribe to chat deletion events
    const unsubscribeDeleted = UserStreamManager.subscribe('CHAT_DELETED', (data) => {
      // A draft must never outlive its conversation — otherwise the localStorage key is an
      // unreachable orphan (nothing can ever open that chat again to clear it).
      // purge, not clear: if the user deleted the chat they were VIEWING, the composer is still
      // mounted and about to unmount-flush. The tombstone makes that flush a no-op.
      if (data?.chat_id) purgeChatDraft(data.chat_id);

      // Remove chat from list
      setChats(prevChats => prevChats.filter(chat => chat.id !== data.chat_id));

      // Update pagination count
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));

      // Emit event for other components
      eventBus.emit('chat-deleted', { chatId: data.chat_id });

      // Project cascades delete many chats at once — one PROJECT_DELETED toast covers
      // them; per-chat toasts would spam the screen.
      if (data?.source !== 'project_deletion') {
        antMessage.success('Chat deleted successfully');
      }
    });

    // NEW: Subscribe to deletion progress (optional - for UI feedback)
    const unsubscribeDeletionProgress = UserStreamManager.subscribe('CHAT_DELETION_PROGRESS', (data) => {
      console.log('[ChatList] Deletion progress:', data);
      // Could show progress toast/notification if desired
    });

    return () => {
      unsubscribeStatus();
      unsubscribeDeleted();
      unsubscribeDeletionProgress();
    };
  }, [currentUser, updateChatInList]);

  // Reset state for disabled users — don't leave loading=true or stale errors
  useEffect(() => {
    if (currentUser && userProfile && userProfile.onboarding_status !== 'JOINED') {
      setChats([]);
      setLoading(false);
      setError(null);
    }
  }, [currentUser, userProfile?.onboarding_status]);

  // Initial fetch of chats
  useEffect(() => {
    if (currentUser && userProfile?.onboarding_status === 'JOINED' && projectFilter === 'all') {
      fetchChats();
    }
  }, [currentUser, userProfile?.onboarding_status]); // Fetch when user logs in or gets enabled

  return (
    <ChatListContext.Provider value={{
      chats,
      loading,
      error,
      pagination,
      refreshChats,
      loadMoreChats,
      updateChatInList,
      addChatToList,
      deleteChat,
      getChatById,
      setSelectedChatId,
      projectFilter,
      availableProjects,
      filterLoading,
      setProjectFilter: handleProjectFilterChange,
      sidebarScrollPositionRef
    }}>
      {children}
    </ChatListContext.Provider>
  );
};

export const useChatList = () => useContext(ChatListContext);
